import React, { useState } from 'react';
import { Siswa, Kelas, KehadiranSiswa, KategoriSiswa } from '../../types/index.js';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Printer,
  RotateCcw,
  Upload,
  FileText,
  CheckCircle2,
  Radio,
  RefreshCw,
  AlertTriangle,
  UserCheck,
} from 'lucide-react';
import { VirtualAccountCardModal } from '../VirtualAccountCardModal.js';
import { UploadCloud, CheckCircle } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { SiswaAttendanceModal } from './SiswaAttendanceModal.js';
import { ImportSiswaModal } from './ImportSiswaModal.js';
import { useDataTable } from '../../hooks/useDataTable.js';
import { api } from '../../services/api.js';
import { Pagination } from '../ui/Pagination.js';
import { SortableHeader } from '../ui/SortableHeader.js';

interface SiswaViewProps {
  siswaList: Siswa[];
  kelasList: Kelas[];
  onAddSiswa: (data: Partial<Siswa>) => Promise<void>;
  onUpdateSiswa: (id: string, data: Partial<Siswa>) => Promise<void>;
  onResetApk: (id: string) => Promise<void>;
  onUpdateAttendance?: (nis: string, data: Partial<KehadiranSiswa>) => Promise<void>;
  onSyncRfid?: () => Promise<void>;
  isSyncingRfid?: boolean;
  onRefresh?: () => Promise<void>;
}

export const SiswaView: React.FC<SiswaViewProps> = ({
  siswaList,
  kelasList,
  onAddSiswa,
  onUpdateSiswa,
  onResetApk,
  onUpdateAttendance,
  onSyncRfid,
  isSyncingRfid = false,
  onRefresh,
}) => {
  const { showAlert, showConfirm } = useDialog();
  const [filterKelas, setFilterKelas] = useState('1 AL BAYAN');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVaModal, setShowVaModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editSiswa, setEditSiswa] = useState<Siswa | null>(null);
  const [attendanceSiswa, setAttendanceSiswa] = useState<Siswa | null>(null);

  // Form state
  const [nis, setNis] = useState('');
  const [nama, setNama] = useState('');
  const [thnAngkatan, setThnAngkatan] = useState('2026');
  const [kelasId, setKelasId] = useState('');
  const [mobile, setMobile] = useState('');
  const [namaWali, setNamaWali] = useState('');
  const [kategori, setKategori] = useState<KategoriSiswa>('REGULER');

  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filteredSiswa, setFilteredSiswa] = useState<Siswa[]>([]);
  const [sortBy, setSortBy] = useState('nama');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  
  const handleSort = (col: string | keyof Siswa) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col as string);
      setSortDir('asc');
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.getSiswaPaginated({
          kelasId: filterKelas === 'ALL' ? undefined : filterKelas,
          search,
          page: currentPage,
          limit: rowsPerPage,
        });
        if (res.success) {
          setFilteredSiswa(res.data);
          setTotalPages(res.totalPages);
          setTotalRows(res.totalCount);
        }
      } catch (err) {
        console.error(err);
      }
    };
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [filterKelas, search, currentPage, rowsPerPage]);

  const handleOpenAdd = () => {
    setEditSiswa(null);
    setNis('');
    setNama('');
    setThnAngkatan('2026');
    setKelasId(kelasList[0]?.id || '');
    setMobile('');
    setNamaWali('');
    setKategori('REGULER');
    setShowAddModal(true);
  };

  const handleOpenEdit = (s: Siswa) => {
    setEditSiswa(s);
    setNis(s.nis);
    setNama(s.nama);
    setThnAngkatan(s.thnAngkatan);
    setKelasId(s.kelasId);
    setMobile(s.mobile);
    setNamaWali(s.namaWali || '');
    setKategori(s.kategori || 'REGULER');
    setShowAddModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editSiswa) {
      const selectedK = kelasList.find((k) => k.id === kelasId);
      await onUpdateSiswa(editSiswa.id, {
        nis,
        nama,
        thnAngkatan,
        kelasId,
        kelasNama: selectedK?.nama || editSiswa.kelasNama,
        mobile,
        namaWali,
        kategori,
      });
    } else {
      await onAddSiswa({ nis, nama, thnAngkatan, kelasId, mobile, namaWali, kategori });
    }
    setShowAddModal(false);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Filter Bar Exact from Document Page 3: Prev By Kelas + Action Buttons */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-slate-900">Manajemen Siswa & Virtual Account</h2>
            <p className="text-xs text-slate-500">
              Pengelolaan biodata siswa, nomor Virtual Account Bank Muamalat/BNI/Duitku & akses User Mobile APK
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleOpenAdd}
              className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Tambah</span>
            </button>

            {onSyncRfid && (
              <button
                onClick={onSyncRfid}
                disabled={isSyncingRfid}
                className="flex items-center gap-1.5 bg-gradient-to-r from-teal-700 to-sky-700 hover:from-teal-600 hover:to-sky-600 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs cursor-pointer disabled:opacity-50"
                title="Tarik log presensi & RFID card dari terminal Solution X107"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncingRfid ? 'animate-spin' : ''}`} />
                <span>{isSyncingRfid ? 'Sinkronisasi Mesin...' : 'Tarik Data RFID (X107)'}</span>
              </button>
            )}

            <button
              onClick={() => setShowVaModal(true)}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Prev VA (Cetak Kartu)</span>
            </button>

            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs cursor-pointer"
              title="Import Excel (.xlsx) Siswa & Virtual Account Massal"
            >
              <Upload className="w-3.5 h-3.5" />
              <span>Upload Siswa (.xlsx)</span>
            </button>
          </div>
        </div>

        {/* Filter controls row (from page 3) */}
        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-600">Prev By Kelas:</span>
            <select
              value={filterKelas}
              onChange={(e) => setFilterKelas(e.target.value)}
              className="text-xs border border-slate-300 rounded-md px-3 py-1.5 bg-slate-50 focus:ring-1 focus:ring-sky-500 font-medium"
            >
              <option value="ALL">-- Semua Kelas --</option>
              {kelasList.map((k) => (
                <option key={k.id} value={k.nama}>
                  {k.nama}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari NIS / Nama / VA..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 w-56"
            />
          </div>
        </div>
      </div>

      {/* Main Table Matching Document Page 3 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-3 w-28">Aksi</th>
                <th className="py-2.5 px-3 w-12 text-center">No</th>
                <SortableHeader label="NIS" columnKey="nis" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Nama Siswa" columnKey="nama" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <th className="py-2.5 px-3 w-24 text-center">Thn Angkatan</th>
                <SortableHeader label="Kelas" columnKey="kelasNama" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <th className="py-2.5 px-3">Kategori</th>
                <SortableHeader label="No. VA" columnKey="noVA" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <th className="py-2.5 px-3">Mobile</th>
                <th className="py-2.5 px-3 text-center">Presensi (X107)</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-center">User APK</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredSiswa.map((s, index) => {
                const att = s.kehadiran;
                const pct = att ? att.persentaseHadir : 95;
                const isKritis = pct < 70;
                const isPerhatian = pct >= 70 && pct < 80;

                return (
                  <tr key={s.id} className="hover:bg-sky-50/50 transition">
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleOpenEdit(s)}
                          className="p-1 text-emerald-700 hover:bg-emerald-50 rounded border border-emerald-300 text-[10px] flex items-center gap-0.5 cursor-pointer"
                          title="Edit Data"
                        >
                          <Edit2 className="w-3 h-3" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => showConfirm(`Hapus data ${s.nama}?`, { type: 'danger', title: 'Hapus Siswa' })}
                          className="p-1 text-rose-700 hover:bg-rose-50 rounded border border-rose-300 text-[10px] flex items-center gap-0.5 cursor-pointer"
                          title="Hapus"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Del</span>
                        </button>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center text-slate-500 font-mono">{(currentPage - 1) * rowsPerPage + index + 1}</td>
                    <td className="py-2 px-3 font-mono font-bold text-slate-800">{s.nis}</td>
                    <td className="py-2 px-4 font-semibold text-slate-900">
                      <div>{s.nama}</div>
                      {s.namaWali && <div className="text-[10px] text-slate-400">Wali: {s.namaWali}</div>}
                    </td>
                    <td className="py-2 px-3 text-center text-slate-600">{s.thnAngkatan}</td>
                    <td className="py-2 px-3 text-slate-800 font-medium">{s.kelasNama}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${
                        s.kategori === 'BEASISWA_PRESTASI'
                          ? 'bg-amber-50 text-amber-900 border-amber-300'
                          : s.kategori === 'BEASISWA_YATIM'
                          ? 'bg-emerald-50 text-emerald-900 border-emerald-300'
                          : s.kategori === 'ANAK_GURU'
                          ? 'bg-indigo-50 text-indigo-900 border-indigo-300'
                          : s.kategori === 'AFIRMASI'
                          ? 'bg-purple-50 text-purple-900 border-purple-300'
                          : 'bg-slate-50 text-slate-700 border-slate-300'
                      }`}>
                        {s.kategori === 'BEASISWA_PRESTASI' ? 'Prestasi' : s.kategori === 'BEASISWA_YATIM' ? 'Yatim' : s.kategori === 'ANAK_GURU' ? 'Anak Guru' : s.kategori === 'AFIRMASI' ? 'Afirmasi' : 'Reguler'}
                      </span>
                    </td>
                    <td className="py-2 px-4 font-mono font-bold text-sky-800 tracking-wider bg-sky-50/40">
                      {s.noVA}
                    </td>
                    <td className="py-2 px-3 font-mono text-slate-600">{s.mobile}</td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => setAttendanceSiswa(s)}
                        className={`px-2 py-1 rounded-lg border text-[11px] font-bold flex items-center gap-1.5 mx-auto transition cursor-pointer shadow-2xs ${
                          isKritis
                            ? 'bg-rose-50 hover:bg-rose-100 border-rose-300 text-rose-800'
                            : isPerhatian
                            ? 'bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-800'
                            : 'bg-emerald-50 hover:bg-emerald-100 border-emerald-300 text-emerald-800'
                        }`}
                        title="Klik untuk melihat / mengedit rincian kehadiran & nomor RFID Solution X107"
                      >
                        <Radio className="w-3 h-3 shrink-0" />
                        <span className="font-mono">{pct}%</span>
                        {isKritis ? (
                          <span className="text-[9px] bg-rose-600 text-white px-1 rounded">KRITIS</span>
                        ) : isPerhatian ? (
                          <span className="text-[9px] bg-amber-600 text-white px-1 rounded">PERHATIAN</span>
                        ) : (
                          <span className="text-[9px] bg-emerald-600 text-white px-1 rounded">BAIK</span>
                        )}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-[10px] font-bold">
                        {s.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => onResetApk(s.id)}
                        className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 rounded text-[10px] font-semibold flex items-center gap-1 mx-auto transition cursor-pointer"
                        title="Reset PIN Login Aplikasi Mobile ke default 123456"
                      >
                        <RotateCcw className="w-2.5 h-2.5" />
                        <span>Reset User APK</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredSiswa.length === 0 && (
                <tr>
                  <td colSpan={12} className="text-center py-8 text-slate-400">
                    Tidak ada data siswa yang cocok dengan filter kelas atau pencarian
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>

      {/* Modal Cetak VA Document (Page 3 exact) */}
      {showVaModal && (
        <VirtualAccountCardModal
          students={siswaList}
          selectedClass={filterKelas === 'ALL' ? undefined : filterKelas}
          onClose={() => setShowVaModal(false)}
        />
      )}

      {/* Modal Tambah / Edit Siswa */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 border border-slate-200">
            <h3 className="text-sm font-bold text-slate-900 mb-4 pb-2 border-b">
              {editSiswa ? 'Edit Biodata Siswa' : 'Tambah Siswa Baru'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">NIS (Nomor Induk)</label>
                  <input
                    type="text"
                    placeholder="Contoh: 2480"
                    value={nis}
                    onChange={(e) => setNis(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Tahun Angkatan</label>
                  <input
                    type="text"
                    value={thnAngkatan}
                    onChange={(e) => setThnAngkatan(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Nama Lengkap Siswa</label>
                <input
                  type="text"
                  placeholder="Contoh: MUHAMMAD ARKA VIRENDRA"
                  value={nama}
                  onChange={(e) => setNama(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md uppercase"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Kelas</label>
                  <select
                    value={kelasId}
                    onChange={(e) => setKelasId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-white"
                  >
                    {kelasList.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.nama}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">No. HP / WA Wali</label>
                  <input
                    type="text"
                    placeholder="081234567890"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Nama Orang Tua / Wali</label>
                  <input
                    type="text"
                    placeholder="Contoh: Budi Santoso"
                    value={namaWali}
                    onChange={(e) => setNamaWali(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Kategori / Jalur Siswa (SPP)</label>
                  <select
                    value={kategori}
                    onChange={(e) => setKategori(e.target.value as KategoriSiswa)}
                    className="w-full px-3 py-2 border rounded-md bg-white font-medium"
                  >
                    <option value="REGULER">Reguler</option>
                    <option value="BEASISWA_PRESTASI">Beasiswa Prestasi</option>
                    <option value="BEASISWA_YATIM">Beasiswa Yatim (Subsidi 50%)</option>
                    <option value="ANAK_GURU">Anak Guru / Pegawai</option>
                    <option value="AFIRMASI">Afirmasi Khusus</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-3 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md font-medium"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-md font-semibold"
                >
                  Simpan Siswa
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Presensi Siswa & Terminal RFID Solution X107 */}
      {attendanceSiswa && (
        <SiswaAttendanceModal
          isOpen={Boolean(attendanceSiswa)}
          siswa={attendanceSiswa}
          onClose={() => setAttendanceSiswa(null)}
          onSaveAttendance={async (nis, data) => {
            if (onUpdateAttendance) {
              await onUpdateAttendance(nis, data);
            }
          }}
          onTriggerRfidSync={onSyncRfid}
          isSyncing={isSyncingRfid}
        />
      )}

      {/* Modal Import Excel Siswa & VA Massal */}
      <ImportSiswaModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        kelasList={kelasList}
        siswaList={siswaList}
        onSuccess={async () => {
          if (onRefresh) {
            await onRefresh();
          }
        }}
      />
    </div>
  );
};
